import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Autocomplete, Chip, FormControl, Icon, TextField } from '@mui/material';

import DialogBase from './DialogBase';
import { Organisatie } from '../../../models/organisatie/organisatie';
import { useNavigate } from 'react-router-dom';
import { planManagementService } from '../../../services/plan-management.service';
import ProgressBar from '../ProgressBar';
import ErrorMessage from '../ErrorMessage';
import { userManagementService } from '../../../services/user-management.service';
import { ListParams } from '../../../models/listParams';

interface DialogStartSharingOrganisatieProps {
  open: boolean;
  title: string;
  contentText?: string;
  sharing: 'plan' | 'gebied';
  planId?: string;
  allowMultiple?: boolean;
  groupBySource?: boolean;
  excludedOrganizationIds?: string[];
  onClose: () => void;
  onSubmit: () => void;
}

interface OrganizationOption {
  id: string;
  label: string;
  group: 'Met gebied' | 'Zonder gebied';
}

const DialogStartSharingOrganisatie: React.FC<DialogStartSharingOrganisatieProps> = ({
  open,
  title,
  contentText,
  sharing,
  planId,
  allowMultiple = false,
  groupBySource = false,
  excludedOrganizationIds = [],
  onClose,
  onSubmit,
}) => {
  const navigate = useNavigate();

  const [options, setOptions] = useState<OrganizationOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<OrganizationOption[]>([]);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const excludedOrganizationIdsKey = JSON.stringify(excludedOrganizationIds);
  const excludedOrganizationIdsSet = useMemo(
    () => new Set<string>(JSON.parse(excludedOrganizationIdsKey) as string[]),
    [excludedOrganizationIdsKey]
  );

  const autocompleteValue = allowMultiple
    ? selectedOptions
    : (selectedOptions[0] ?? null);

  const fetchOrganisatiesToShare = useCallback(async () => {
    setLoading(true);
    try {
      const storedOrganisatie = userManagementService.getStoredOrganisatie();

      const params: ListParams = {
        offset: 0,
        limit: 250,
      };
      
      // 'gebied' → sharing/organizations (alleen met-gebied orgs)
      // 'plan'   → plan-sharing/organizations (met én zonder-gebied orgs)
      const isSharePerPlan = sharing === 'plan';

      const data = await planManagementService.getOrganisatiesToShare(params, isSharePerPlan, { fetchAll: true });

      let organisaties: Organisatie[] = Array.isArray(data?.items) ? data.items : [];
      if (storedOrganisatie?.id) {
        organisaties = organisaties.filter((x: Organisatie) => x.id !== storedOrganisatie.id);
      }

      if (excludedOrganizationIdsSet.size > 0) {
        organisaties = organisaties.filter((item) => item.id && !excludedOrganizationIdsSet.has(item.id));
      }

      const mappedOptions = organisaties
        .map((item) => {
          if (!item.id) {
            return null;
          }

          const resolvedGroup: 'Met gebied' | 'Zonder gebied' = item.organizationType === 'zonder-gebied'
            ? 'Zonder gebied'
            : 'Met gebied';

          return {
            id: item.id,
            label: item.displayName || item.name || item.id,
            group: resolvedGroup,
          } as OrganizationOption;
        })
        .filter((item): item is OrganizationOption => Boolean(item))
        .sort((a, b) => {
          if (a.group !== b.group) {
            return a.group.localeCompare(b.group, 'nl-NL');
          }
          return a.label.localeCompare(b.label, 'nl-NL');
        });

      setOptions(mappedOptions);
      setSelectedOptions([]);
      setSelectionError(null);
    }
    catch (error: any) {
      // If token refresh failed, redirect to the logout page
      if (error.message === 'Unable to refresh token') {
        navigate('/verlopen');
      }
      setError(error.message || 'Er is een fout opgetreden');
    }
    finally {
      setLoading(false);
    }
  }, [excludedOrganizationIdsSet, navigate, sharing]);

  useEffect(() => {
    if (open) {
      fetchOrganisatiesToShare();
    }
    else {
      setSelectedOptions([]);
      setSelectionError(null);
    }
  }, [fetchOrganisatiesToShare, open]);

  const handleSubmit = async () => {
    if (selectedOptions.length === 0) {
      setSelectionError('Selecteer minimaal 1 organisatie.');
      return;
    }

    try {
      setLoading(true);
      setSelectionError(null);

      const ids = selectedOptions.map((item) => item.id);
      await Promise.all(ids.map((organizationId) => (
        planId
          ? planManagementService.addSharePlanWithOrganisation(planId, organizationId)
          : planManagementService.addSharedOrganisatie(organizationId)
      )));

      onSubmit();
      onClose();
      setSelectedOptions([]);
    }
    catch (error) {
      console.error('Error sharing organisatie:', error);
      setError('Er is een fout opgetreden');
    }
    finally {
      setLoading(false);
    }
  };

  return (
    <DialogBase
      open={open}
      title={title}
      contentText={contentText}
      onClose={onClose}
      onConfirm={handleSubmit}
      confirmButtonText="Start met delen"
      confirmButtonIcon={<Icon>rocket_launch</Icon>}
    >
      <section>
        <FormControl fullWidth required sx={{ my: 1 }}>
          <Autocomplete
            multiple={allowMultiple}
            options={options}
            value={autocompleteValue}
            disabled={loading}
            noOptionsText="Geen keuzes beschikbaar"
            loadingText="Laden..."
            onChange={(_event, value) => {
              const normalized = Array.isArray(value) ? value : value ? [value] : [];
              setSelectedOptions(normalized);
              if (normalized.length > 0) {
                setSelectionError(null);
              }
            }}
            groupBy={groupBySource ? (option) => option.group : undefined}
            getOptionLabel={(option) => {
              if (typeof option === 'string') {
                return option;
              }
              if (Array.isArray(option)) {
                return '';
              }
              return option?.label || '';
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterSelectedOptions={allowMultiple}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip variant="filled" label={option.label} {...getTagProps({ index })} key={option.id} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label={allowMultiple ? 'Organisaties toevoegen' : 'Kies een organisatie'}
                variant="filled"
                placeholder={allowMultiple ? 'Zoek en selecteer één of meerdere organisaties' : 'Zoek en selecteer een organisatie'}
                error={Boolean(selectionError)}
                helperText={selectionError || undefined}
              />
            )}
          />
        </FormControl>

      </section>

      { loading && (
        <ProgressBar />
      )}

      { error && (
        <ErrorMessage message={error} />
      )}
      
    </DialogBase>
  );
};

export default DialogStartSharingOrganisatie;
